import { makeObservable, observable, action } from 'mobx';
import { LocalStorageService } from './LocalStorageService';
import { STORAGE_KEYS } from '../../constants/constants';

/**
 * 视图-用户选择管理服务
 * 专门负责管理视图和用户的关联选择，包括持久化和恢复
 */
export class ViewUserSelectionManager {
  constructor(storageService = new LocalStorageService()) {
    this._storageService = storageService;
    this._viewUserSelections = new Map(); // 内存缓存，提高性能

    makeObservable(this, {
      _viewUserSelections: observable,
      saveViewUserSelection: action,
      restoreViewUserSelection: action,
      clearViewUserSelection: action,
      getDefaultUserForView: action,
    });

    // 初始化时从localStorage加载数据
    this._loadFromStorage();
  }

  /**
   * 从localStorage加载数据到内存缓存
   */
  _loadFromStorage() {
    const data = this._storageService.get(STORAGE_KEYS.VIEW_USER_SELECTIONS);
    if (data && typeof data === 'object') {
      Object.entries(data).forEach(([viewId, selection]) => {
        this._viewUserSelections.set(viewId, selection);
      });
    }
  }

  /**
   * 保存视图-用户选择到localStorage
   */
  _saveToStorage() {
    const data = {};
    this._viewUserSelections.forEach((selection, viewId) => {
      data[viewId] = selection;
    });
    this._storageService.save(STORAGE_KEYS.VIEW_USER_SELECTIONS, data);
  }

  /**
   * 保存视图-用户选择
   * @param {string} viewId - 视图ID
   * @param {string} chatUserId - 聊天用户ID
   */
  saveViewUserSelection(viewId, chatUserId) {
    if (!viewId || !chatUserId) {
      return;
    }

    const selection = {
      chatUserId,
      timestamp: Date.now(),
    };

    this._viewUserSelections.set(viewId, selection);
    this._saveToStorage();
  }

  /**
   * 恢复视图-用户选择
   * @param {string} viewId - 视图ID
   * @returns {string|null} 聊天用户ID，如果没有选择过则返回null
   */
  restoreViewUserSelection(viewId) {
    if (!viewId) {
      return null;
    }

    const selection = this._viewUserSelections.get(viewId);
    if (!selection) {
      return null;
    }

    return selection.chatUserId;
  }

  /**
   * 清除指定视图的用户选择
   * @param {string} viewId - 视图ID
   */
  clearViewUserSelection(viewId) {
    if (this._viewUserSelections.has(viewId)) {
      this._viewUserSelections.delete(viewId);
      this._saveToStorage();
    }
  }

  /**
   * 获取视图下的默认用户
   * 如果用户之前选择过，返回选择的用户；否则返回第一个用户
   * @param {string} viewId - 视图ID
   * @param {Array} viewData - 视图数据
   * @returns {Object|null} 用户对象，包含用户信息和是否需要滚动
   */
  getDefaultUserForView(viewId, viewData) {
    if (!viewId || !viewData || !Array.isArray(viewData.data) || viewData.data.length === 0) {
      return null;
    }

    // 尝试恢复之前选择的用户
    const lastSelectedUserId = this.restoreViewUserSelection(viewId);

    if (lastSelectedUserId) {
      const lastSelectedUser = viewData.data.find(
        user => String(user.chat_user_id) === String(lastSelectedUserId)
      );

      if (lastSelectedUser) {
        return {
          user: lastSelectedUser,
          needScroll: true, // 需要滚动到该用户位置
          reason: 'restored',
        };
      } else {
        // 用户不存在，清除过期选择
        this.clearViewUserSelection(viewId);
      }
    }

    // 没有选择过或选择无效，返回第一个用户
    const firstUser = viewData.data[0];

    return {
      user: firstUser,
      needScroll: false, // 第一个用户不需要滚动
      reason: 'first_user',
    };
  }

  /**
   * 检查视图是否有用户选择记录
   * @param {string} viewId - 视图ID
   * @returns {boolean} 是否有选择记录
   */
  hasViewUserSelection(viewId) {
    return this._viewUserSelections.has(viewId);
  }

  /**
   * 获取所有视图的用户选择统计
   * @returns {Object} 统计信息
   */
  getSelectionStats() {
    return {
      totalSelections: this._viewUserSelections.size,
      views: Array.from(this._viewUserSelections.keys()),
    };
  }

  /**
   * 清理过期的选择记录（可选，用于维护）
   * @param {number} maxAge - 最大保留时间（毫秒），默认30天
   */
  cleanupExpiredSelections(maxAge = 30 * 24 * 60 * 60 * 1000) {
    const now = Date.now();
    const expiredViews = [];

    this._viewUserSelections.forEach((selection, viewId) => {
      if (now - selection.timestamp > maxAge) {
        expiredViews.push(viewId);
      }
    });

    expiredViews.forEach(viewId => {
      this.clearViewUserSelection(viewId);
    });
  }
}
